// Comercial → Pipeline de Negócios (funil + lista + kanban + calendário + KPIs).
//
// Entry fino. Após refactor de 2026-05-23 (frontend wave 3B), o kanban, forms
// quick-create (cliente/contato/negócio) e modal de gerar proposta foram
// movidos pra ./comercial-pipeline/.
//
// 01/10/2026 — as três vistas e o funil foram redesenhados no padrão dos CRMs
// de referência (pedido do Lucas). O que cada peça faz e por quê está no
// cabeçalho dela e em docs/pipeline-crm.md; a régua visual (negócio parado,
// próximo passo, funil) é pura e mora em comercial-pipeline/saudeNegocio.ts.
//
// 01/10/2026 (tarde) — Lucas: "muito cabeçalho sobre cabeçalho e o funil lá
// embaixo espremido". Acima do quadro havia quatro andares (cartões de
// indicadores, filtros, faixa do funil, atalhos) e o mesmo número aparecia três
// vezes. Ficaram dois: a barra de filtros e UMA linha de resumo. No kanban o
// funil é o cabeçalho das colunas; a faixa de setas só aparece na lista.
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Segmented, Tooltip, App as AntApp } from 'antd';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import NegocioDetalhe from './NegocioDetalhe';
import TelaLicitacao from './comercial-licitacoes/TelaLicitacao';
import { Proposta, Cliente, KPIs } from './comercial-pipeline/types';
import { KanbanPipeline } from './comercial-pipeline/KanbanPipeline';
import { ListaPipeline } from './comercial-pipeline/ListaPipeline';
import { CalendarioPipeline } from './comercial-pipeline/CalendarioPipeline';
import { DrawerNegocio } from './comercial-pipeline/DrawerNegocio';
import { FormNovoNegocio } from './comercial-pipeline/FormNovoNegocio';
import { opcoesClienteDoPipeline, casaFiltroCliente } from './comercial-pipeline/filtroCliente';
import { FiltrosPipeline } from './comercial-pipeline/FiltrosPipeline';
import { FunilEtapas, ResumoPipeline } from './comercial-pipeline/FunilEtapas';
import {
  casaSinal, contarSinais, ehAberto, resumoDoFunil,
  type Ordenacao, type ProximoPasso, type Sinal,
} from './comercial-pipeline/saudeNegocio';
import { ModalPerder } from './negocio-detalhe/ModalPerder';

// Re-export pra manter retrocompatibilidade com NegocioDetalhe (que importa daqui)
export { ModalGerarProposta } from './comercial-pipeline/ModalGerarProposta';

type Vista = 'lista' | 'kanban' | 'calendario';

function lerSessao<T extends string>(chave: string, validos: readonly T[], padrao: T): T {
  try {
    const v = sessionStorage.getItem(chave) as T | null;
    return v && validos.includes(v) ? v : padrao;
  } catch { return padrao; }
}

function ComercialPipelineInner() {
  const { message, modal } = AntApp.useApp();
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [loading, setLoading] = useState(true);
  // Próximo passo agendado por negócio. Era lido dentro do kanban; subiu para a
  // página porque a lista, o calendário e os atalhos de cobrança também usam.
  const [passos, setPassos] = useState<Record<number, ProximoPasso>>({});
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  // Filtro por cliente (pedido do Lucas, 30/09/2026). O valor e o ID do cliente
  // em texto (ou SEM_EMPRESA) — nunca o nome, que se repete entre filiais.
  const [filtroCliente, setFiltroCliente] = useState('');
  const [busca, setBusca] = useState('');
  // Atalho de cobrança: "sem próximo passo", "parados", "sem responsável".
  const [sinal, setSinal] = useState<Sinal>('');
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(
    () => lerSessao('pipeline_ordenacao', ['valor', 'parados', 'recentes'] as const, 'valor'));
  const [editando, setEditando] = useState<Proposta | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [espiando, setEspiando] = useState<number | null>(null);
  const [licitacoesFora, setLicitacoesFora] = useState(0);
  const [perdendo, setPerdendo] = useState<Proposta | null>(null);
  const [negocioAberto, setNegocioAberto] = useState<number | null>(() => {
    // Permite outra aba (ex: Licitações) sinalizar qual proposta abrir.
    // O id fica em sessionStorage e é consumido aqui no mount.
    const id = sessionStorage.getItem('abrir_proposta_id');
    if (id) {
      sessionStorage.removeItem('abrir_proposta_id');
      return parseInt(id, 10) || null;
    }
    return null;
  });
  // Incrementa ao arrastar um card pra "Fechamento" → NegocioDetalhe abre o
  // modal "Ganhar" assim que carrega (atalho do kanban pro fluxo de venda).
  const [autoGanharTick, setAutoGanharTick] = useState(0);
  const [vista, setVista] = useState<Vista>(
    () => lerSessao('pipeline_vista', ['lista', 'kanban', 'calendario'] as const, 'lista'));
  useEffect(() => { try { sessionStorage.setItem('pipeline_vista', vista); } catch { /* sem storage */ } }, [vista]);
  useEffect(() => { try { sessionStorage.setItem('pipeline_ordenacao', ordenacao); } catch { /* sem storage */ } }, [ordenacao]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, []);

  useEffect(() => {
    if (loading || propostas.length === 0) return;
    const id = sessionStorage.getItem('pipeline_destacar_proposta_id');
    if (!id) return;
    const p = propostas.find(x => String(x.id) === String(id));
    if (p) { setEditando(p); setShowForm(true); }
    sessionStorage.removeItem('pipeline_destacar_proposta_id');
  }, [loading, propostas]);

  // L1 fix (2026-06-02): atalho pra abrir uma proposta especifica vindo de
  // outra pagina (ex.: clica "Ver proposta #N" no modal de Licitacao). Antes
  // so funcionava no mount inicial via sessionStorage (useState inicializador
  // nao re-executa). Agora qualquer pagina pode emitir o event que abrimos
  // a tela do negocio na hora.
  useEffect(() => {
    function aoAbrirProposta(e: Event) {
      const detail = (e as CustomEvent).detail;
      const id = parseInt(String(detail?.propostaId || ''), 10);
      if (id) {
        sessionStorage.removeItem('abrir_proposta_id');
        setNegocioAberto(id);
      }
    }
    window.addEventListener('abrir-proposta-no-pipeline', aoAbrirProposta);
    return () => window.removeEventListener('abrir-proposta-no-pipeline', aoAbrirProposta);
  }, []);

  async function carregar(mostrarLoading = true) {
    if (mostrarLoading) setLoading(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      const [propRes, kpiRes, clRes] = await Promise.all([
        axios.get(`${API_URL}/comercial/propostas`, { headers }),
        axios.get(`${API_URL}/comercial/kpis`, { headers }),
        axios.get(`${API_URL}/comercial/clientes`, { headers }),
      ]);
      const propData = propRes.data.body ? JSON.parse(propRes.data.body) : propRes.data;
      const kpiData = kpiRes.data.body ? JSON.parse(kpiRes.data.body) : kpiRes.data;
      const clData = clRes.data.body ? JSON.parse(clRes.data.body) : clRes.data;
      // 02/10/2026: licitação tem pipeline próprio (Comercial › Licitações ›
      // Pipeline). O negócio gêmeo dela continua existindo para preço, venda e
      // contrato, mas não aparece aqui — cada funil mostra só o que é dele.
      const todas: Proposta[] = Array.isArray(propData) ? propData : [];
      setLicitacoesFora(todas.filter(p => p.origem === 'licitacao' && ehAberto(p.status)).length);
      setPropostas(todas.filter(p => p.origem !== 'licitacao'));
      setKpis(kpiData);
      setClientes(clData);
      carregarPassos(propData);
    } catch (e: any) {
      console.error('Erro pipeline:', e);
    } finally { if (mostrarLoading) setLoading(false); }
  }

  // Só negócio ABERTO tem próximo passo a cobrar — e são 181 ids em vez de 479.
  // ⚠️ Falha aqui NÃO pode virar "ninguém tem próximo passo": o estado antigo
  // fica, e a tela não acusa 181 negócios por causa de uma leitura que caiu.
  async function carregarPassos(lista: Proposta[] = propostas) {
    const ids = lista.filter(p => ehAberto(p.status)).map(p => p.id).filter(Boolean);
    if (ids.length === 0) { setPassos({}); return; }
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'com_resumo_followups_negocios', negocio_ids: ids },
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (data && typeof data === 'object' && !data.error) setPassos(data);
    } catch (e) { console.error('Erro ao ler próximos passos:', e); }
  }

  async function gravarStatus(id: number, corpo: Record<string, any>) {
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/comercial/propostas/${id}`, corpo, { headers: { Authorization: token } });
      await carregar(false);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function atualizarStatus(id: number, novoStatus: string) {
    // VND-FIX (2026-06-09): ganho só pelo botão "Ganhar" (gera a venda) e
    // negócio ganho não sai de ganho pelo kanban (incidente Beralv — arrasto
    // acidental reescreveu a data da venda). Backend também bloqueia.
    const p = propostas.find(x => x.id === id);
    if (p?.status === novoStatus) return;
    if (novoStatus === 'ganho') {
      // Atalho (2026-06-18): em vez de só avisar, abre o negócio já disparando
      // o fluxo "Ganhar" (que gera a venda do jeito certo — VND-FIX). A venda
      // só é criada de fato ao confirmar no modal, então a proteção continua.
      message.info('Abrindo o negócio pra gerar a venda…');
      setEspiando(null);
      setNegocioAberto(id);
      setAutoGanharTick(t => t + 1);
      return;
    }
    if (p?.status === 'ganho') {
      message.warning('Negócio ganho não volta de etapa pelo kanban. Use "Desmarcar Ganho" dentro do negócio.');
      return;
    }
    // Perder pergunta o motivo antes de gravar — por qualquer porta (arrastar,
    // seletor da lista, trilha da espiada).
    if (novoStatus === 'perdido' && p) { setPerdendo(p); return; }
    await gravarStatus(id, { status: novoStatus });
  }

  function irParaComposicao(p: Proposta) {
    sessionStorage.setItem('nova_composicao_intent', JSON.stringify({ proposta_id: p.id, proposta_titulo: p.titulo }));
    window.dispatchEvent(new CustomEvent('app-navigate', {
      detail: { modulo: 'comercial', pagina: 'com-composicoes' },
    }));
  }

  function irParaPropostasGeradas(p: Proposta) {
    sessionStorage.setItem('filtrar_propostas_negocio_id', String(p.id));
    sessionStorage.setItem('filtrar_propostas_negocio_titulo', p.titulo);
    window.dispatchEvent(new CustomEvent('app-navigate', {
      detail: { modulo: 'comercial', pagina: 'com-propostas' },
    }));
  }

  function excluirProposta(id: number) {
    modal.confirm({
      title: 'Excluir esta proposta?', okText: 'Excluir', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/comercial/propostas/${id}`,
            { headers: { Authorization: token } });
          await carregar(false);
        } catch (e: any) {
          const msg = e.response?.data?.error || e.message;
          if (e.response?.status === 409) {
            modal.warning({ title: 'Não dá pra excluir ainda', content: msg, width: 520 });
          } else {
            message.error('Erro: ' + msg);
          }
        }
      },
    });
  }

  // No calendário a linha de resumo (onde moram os atalhos) não aparece — ele
  // tem o próprio resumo do mês. Um atalho ligado em outra vista não pode
  // continuar filtrando ali, invisível.
  const sinalAtivo: Sinal = vista === 'calendario' ? '' : sinal;
  // UM predicado para os tres modos de ver (lista, kanban, calendario) E para o
  // funil — com `ignorarStatus`, porque as etapas do funil sao justamente quem
  // liga e desliga o status.
  function passaFiltros(p: Proposta, ignorarStatus = false): boolean {
    if (!ignorarStatus && filtroStatus && p.status !== filtroStatus) return false;
    if (!casaFiltroCliente(p, filtroCliente)) return false;
    if (filtroResponsavel && (p.responsavel || '') !== filtroResponsavel) return false;
    if (!casaSinal(p, sinalAtivo, passos[p.id])) return false;
    if (busca) {
      const q = busca.toLowerCase();
      const matchTitulo = (p.titulo || '').toLowerCase().includes(q);
      const matchCliente = (p.cliente_nome || (p as any).razao_social || '').toLowerCase().includes(q);
      const matchResp = (p.responsavel || '').toLowerCase().includes(q);
      const matchArea = (p.area || '').toLowerCase().includes(q);
      const matchObs = (p.observacoes || '').toLowerCase().includes(q);
      if (!matchTitulo && !matchCliente && !matchResp && !matchArea && !matchObs) return false;
    }
    return true;
  }
  const filtradas = propostas.filter(p => passaFiltros(p));
  // O funil conta DENTRO dos outros filtros: com um cliente escolhido, ele mostra
  // em que etapa estao os negocios DELE.
  const baseDoFunil = propostas.filter(p => passaFiltros(p, true));
  const resumo = resumoDoFunil(baseDoFunil);
  // Os atalhos de cobranca contam dentro dos filtros de cima, mas IGNORAM o
  // proprio atalho — senao, ao ligar um, os outros dois zerariam.
  const baseDosSinais = propostas.filter(p => {
    if (filtroStatus && p.status !== filtroStatus) return false;
    if (!casaFiltroCliente(p, filtroCliente)) return false;
    if (filtroResponsavel && (p.responsavel || '') !== filtroResponsavel) return false;
    return true;
  });
  const contagemSinais = contarSinais(baseDosSinais, passos);
  const responsaveisExistentes = Array.from(new Set(propostas.map(p => p.responsavel).filter(Boolean))).sort() as string[];
  // Somente clientes QUE TEM negocio: o cadastro tem 330 ativos contra 186 com
  // negocio (medido em 30/09/2026) — 144 opcoes que nunca filtrariam nada.
  const opcoesCliente = opcoesClienteDoPipeline(propostas, clientes);
  const temFiltro = !!(busca || filtroStatus || filtroResponsavel || filtroCliente || sinalAtivo);
  function limparFiltros() {
    setBusca(''); setFiltroStatus(''); setFiltroResponsavel('');
    setFiltroCliente(''); setSinal('');
  }

  if (negocioAberto) {
    // Se a proposta tem origem='licitacao', abre a tela dedicada (F3)
    // em vez do NegocioDetalhe padrão (que assume proposta privada).
    const propAberta = propostas.find(p => p.id === negocioAberto);
    if (propAberta?.origem === 'licitacao') {
      return (
        <TelaLicitacao
          propostaId={negocioAberto}
          onVoltar={async () => { setNegocioAberto(null); await carregar(); }}
        />
      );
    }
    return (
      <NegocioDetalhe
        negocioId={negocioAberto}
        autoGanharTick={autoGanharTick}
        onVoltar={async () => { setNegocioAberto(null); await carregar(); }}
      />
    );
  }

  const negocioEspiado = espiando != null ? propostas.find(p => p.id === espiando) || null : null;

  return (
    <div style={{ margin: '12px auto', padding: '0 24px' }}>
      <FiltrosPipeline
        vista={vista} setVista={setVista}
        filtroStatus={filtroStatus} setFiltroStatus={setFiltroStatus}
        filtroResponsavel={filtroResponsavel} setFiltroResponsavel={setFiltroResponsavel}
        filtroCliente={filtroCliente} setFiltroCliente={setFiltroCliente}
        busca={busca} setBusca={setBusca}
        responsaveis={responsaveisExistentes}
        opcoesCliente={opcoesCliente}
        qtdFiltrada={filtradas.length} qtdTotal={propostas.length}
        temFiltro={temFiltro} onLimpar={limparFiltros}
        onNovoBriefing={() => window.dispatchEvent(new CustomEvent('dwight:novo-briefing'))}
        onNovoNegocio={() => { setEditando(null); setShowForm(true); }}
      />

      {licitacoesFora > 0 && (
        <div style={{ fontSize: 12, color: '#5A6378', marginBottom: 8 }}>
          <Link to="/comercial/licitacoes?aba=pipeline" style={{ color: '#0930D0' }}>
            {licitacoesFora} {licitacoesFora === 1 ? 'licitação em aberto está' : 'licitações em aberto estão'} no pipeline de licitações →
          </Link>
        </div>
      )}
      {!loading && propostas.length > 0 && vista !== 'calendario' && (
        <>
          <ResumoPipeline resumo={resumo} contagem={contagemSinais} sinal={sinal} setSinal={setSinal} kpis={kpis}
            direita={vista === 'kanban' ? (
              <Tooltip title="Ordem dos cartões dentro de cada coluna">
                <Segmented size="small" value={ordenacao} onChange={(v) => setOrdenacao(v as Ordenacao)}
                  options={[
                    { value: 'valor', label: 'Maior valor' },
                    { value: 'parados', label: 'Parados' },
                    { value: 'recentes', label: 'Recentes' },
                  ]} />
              </Tooltip>
            ) : undefined} />
          {vista === 'lista' && (
            <FunilEtapas resumo={resumo} filtroStatus={filtroStatus} setFiltroStatus={setFiltroStatus} />
          )}
        </>
      )}

      {vista === 'calendario' ? (
        <CalendarioPipeline propostas={filtradas} passos={passos}
          onAbrir={p => setNegocioAberto(p.id)} />
      ) : vista === 'kanban' ? (
        <KanbanPipeline propostas={filtradas} passos={passos} loading={loading} ordenacao={ordenacao}
          onAbrir={p => setNegocioAberto(p.id)}
          onEspiar={p => setEspiando(p.id)}
          onMudarStatus={atualizarStatus}
          onIrComposicao={irParaComposicao}
          onExcluir={excluirProposta} />
      ) : (
        <ListaPipeline propostas={filtradas} passos={passos} loading={loading}
          onAbrir={p => setNegocioAberto(p.id)}
          onEspiar={p => setEspiando(p.id)}
          onMudarStatus={atualizarStatus}
          onIrComposicao={irParaComposicao}
          onIrPropostas={irParaPropostasGeradas}
          onExcluir={excluirProposta} />
      )}

      {negocioEspiado && (
        <DrawerNegocio negocio={negocioEspiado}
          onFechar={() => setEspiando(null)}
          onAbrirCompleto={() => { const id = negocioEspiado.id; setEspiando(null); setNegocioAberto(id); }}
          onMudarStatus={atualizarStatus}
          onMudou={() => carregarPassos()} />
      )}

      {perdendo && (
        <ModalPerder titulo={perdendo.titulo} onCancelar={() => setPerdendo(null)}
          onConfirmar={async (motivo) => {
            await gravarStatus(perdendo.id, { status: 'perdido', motivo_perda: motivo });
            setPerdendo(null);
          }} />
      )}

      {showForm && !editando && (
        <FormNovoNegocio
          clientes={clientes}
          onClose={() => setShowForm(false)}
          onCriado={async (criado) => {
            setShowForm(false);
            await carregar();
            if (criado?.id) setNegocioAberto(criado.id);
          }}
        />
      )}
    </div>
  );
}

export default function ComercialPipeline() {
  return (
    <AntApp>
      <ComercialPipelineInner />
    </AntApp>
  );
}
